import { useEffect, useState } from "react";
import axios from "axios";
import { Cloud, CheckCircle2, XCircle, RefreshCw, Activity, Power } from "lucide-react";
import { API } from "../../config";

export const SalesforceHealthWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API}/salesforce/status`);
      setData(r.data);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await axios.post(`${API}/salesforce/test-connection`);
      setTestResult({ ok: true, msg: r.data?.message || "Connessione OK" });
    } catch (e) {
      setTestResult({ ok: false, msg: e?.response?.data?.detail || "Connessione fallita" });
    } finally {
      setTesting(false);
    }
  };

  useEffect(() => {
    fetchData();
    const t = setInterval(fetchData, 90_000);
    return () => clearInterval(t);
  }, []);

  if (!data) {
    return (
      <div className="bg-[#111] border border-gray-800 rounded-2xl p-6" data-testid="sf-health-widget">
        {loading ? <div className="spinner mx-auto" /> : <p className="text-gray-500 text-sm">Nessun dato</p>}
      </div>
    );
  }

  const stats = data.stats || {};
  const total = stats.total_syncs || 0;
  const success = stats.successful || 0;
  const failed = stats.failed || 0;
  const successRate = total > 0 ? Math.round((success / total) * 100) : 100;
  const rateColor = successRate >= 95 ? "text-lojack-blue-400" : successRate >= 80 ? "text-lojack-clause-400" : "text-red-400";

  return (
    <div className="bg-gradient-to-br from-[#0f1422] via-[#0a0d18] to-[#0f1422] border border-sky-500/20 rounded-2xl p-6 space-y-5" data-testid="sf-health-widget">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-sky-500/10 flex items-center justify-center">
            <Cloud size={20} className="text-sky-400" />
          </div>
          <div>
            <h3 className="text-white font-bold text-lg">CRM Sync Health</h3>
            <p className="text-gray-500 text-xs">Integrazione CRM aziendale · sincronizzazione clienti firmati</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-xs flex items-center gap-1.5 ${data.configured && data.enabled ? "text-lojack-blue-400" : "text-gray-500"}`}>
            <span className={`w-2 h-2 rounded-full ${data.configured && data.enabled ? "bg-lojack-blue-500 animate-pulse" : "bg-gray-600"}`} />
            {data.configured && data.enabled ? "Attivo" : data.configured ? "Configurato (disattivo)" : "Non configurato"}
          </span>
          <button
            onClick={runTest}
            disabled={testing || !data.configured}
            className="p-2 text-gray-500 hover:text-sky-400 transition-colors disabled:opacity-30"
            title="Test connessione"
            data-testid="sf-test-connection"
          >
            <Power size={16} className={testing ? "animate-pulse" : ""} />
          </button>
          <button
            onClick={fetchData}
            disabled={loading}
            className="p-2 text-gray-500 hover:text-sky-400 transition-colors"
            data-testid="sf-refresh"
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {testResult && (
        <div className={`text-xs p-2.5 rounded-lg ${testResult.ok ? "bg-lojack-blue-500/10 text-lojack-blue-300 border border-lojack-blue-500/30" : "bg-red-500/10 text-red-300 border border-red-500/30"}`}>
          {testResult.ok ? "✓ " : "✗ "}{testResult.msg}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-black/40 border border-gray-800 rounded-xl p-4" data-testid="sf-total">
          <p className="text-gray-400 text-xs uppercase tracking-wider mb-2">Sync totali</p>
          <p className="text-3xl font-bold text-white">{total}</p>
          <p className="text-gray-500 text-[10px] mt-2">Tipologia: <span className="text-gray-300">{data.object_type || "Lead"}</span></p>
        </div>

        <div className="bg-black/40 border border-gray-800 rounded-xl p-4" data-testid="sf-success">
          <div className="flex items-center gap-1.5 mb-2"><CheckCircle2 size={12} className="text-lojack-blue-400" /><p className="text-gray-400 text-xs uppercase tracking-wider">Riusciti</p></div>
          <p className="text-3xl font-bold text-lojack-blue-400">{success}</p>
          <p className="text-gray-500 text-[10px] mt-2">Su {total} totali</p>
        </div>

        <div className="bg-black/40 border border-gray-800 rounded-xl p-4" data-testid="sf-failed">
          <div className="flex items-center gap-1.5 mb-2"><XCircle size={12} className="text-red-400" /><p className="text-gray-400 text-xs uppercase tracking-wider">Falliti</p></div>
          <p className="text-3xl font-bold text-red-400">{failed}</p>
          <p className="text-gray-500 text-[10px] mt-2">Da rivedere</p>
        </div>

        <div className="bg-black/40 border border-gray-800 rounded-xl p-4" data-testid="sf-rate">
          <div className="flex items-center gap-1.5 mb-2"><Activity size={12} className={rateColor} /><p className="text-gray-400 text-xs uppercase tracking-wider">Success Rate</p></div>
          <p className={`text-3xl font-bold ${rateColor}`}>{successRate}%</p>
          {stats.last_successful_sync && (
            <p className="text-gray-500 text-[10px] mt-2 truncate" title={stats.last_successful_sync}>
              Ultimo OK: {new Date(stats.last_successful_sync).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}
            </p>
          )}
        </div>
      </div>

      <p className="text-gray-600 text-[10px] text-center">
        Auto-refresh ogni 90s · Account: {data.username || "non configurato"}
      </p>
    </div>
  );
};

export default SalesforceHealthWidget;
